import type { Custodian } from 'models/custodian';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { MapStateToPropsFactory } from 'react-redux';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { secondaryMarketCpOrderFormFieldChanged } from 'state/secondaryMarket/cpOrder';
import {
  fromSettlementTypeKey,
  getAmountUnit,
  getCobDate,
  getRelevantPriceDependingOnTradeWay,
  getSalesMarginPlusUpfront,
  isOtcBcsProductKey,
} from 'state/secondaryMarket/cpOrder/cpOrder.utils';
import { getMaxQuantity } from 'state/secondaryMarket/cpOrder/cpOrderForm/cpOrderForm.selectors';
import {
  secondaryMarketCpOrderCashAmountChangedThunk,
  secondaryMarketCpOrderCashAmountResetThunk,
  secondaryMarketCpOrderClientSelectedThunk,
  secondaryMarketCpOrderClientTypeSelectedThunk,
  secondaryMarketCpOrderCustodianSelectedThunk,
  secondaryMarketCpOrderFormTradeDateTypeChangedThunk,
  secondaryMarketCpOrderFormValuationDateChangedThunk,
  secondaryMarketCpOrderQuantityChangedThunk,
  secondaryMarketCpOrderQuantityResetThunk,
  secondaryMarketCpOrderSelectSalesDeskThunk,
  secondaryMarketCpOrderSelectSalesIdThunk,
  secondaryMarketCpOrderTradeSizeInputModeChangedThunk,
} from 'state/secondaryMarket/cpOrder/cpOrderForm/cpOrderForm.thunks';
import type { TradeDateType } from 'state/secondaryMarket/cpOrder/cpOrderForm/input/cpOrderInputForm.model';
import { secondaryMarketCpFormLinkedToClientRequested } from 'state/secondaryMarket/cpOrder/referential/client/client.actions';
import type { CpOrderSettlement, SalesDesk } from 'state/secondaryMarket/cpOrder/referential/client/client.model';
import {
  getCpOrderReferentialLotSize,
  getCpOrderReferentialMinQuantity,
  getCpOrderReferentialRemaining,
} from 'state/secondaryMarket/cpOrder/referential/product/product.selector';
import { getAllowedTradeWays } from 'state/secondaryMarket/cpOrder/referential/product/product.selector.bcs';
import type { SecondaryPriceType, SecondaryQuotationType } from 'state/secondaryMarket/productSearch';
import { allTradeWays, type TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import type { ClientType } from 'state/secondaryMarket/shared/form.models';
import type { OtcClient, Sales } from 'state/secondaryMarket/shared/referential/referential.model';
import type { TradeSizeInputMode } from 'state/secondaryMarket/shared/tradeInformation.models';
import { toSettlementTypeKey } from 'state/secondaryMarket/shared/utils';
import { getAvailableDesks, getAvailableSalesWithSelecteDesk } from 'state/secondaryMarket/utils';
import { fromSecondaryMarket, fromSecondaryPostEventsWorkspace } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { WorkspaceType } from 'state/workspaces/model';
import { getTabIsFrozen } from 'state/workspaces/tabs/tabs.selector';
import type { LoadingStatus } from 'utils/status';

type OwnProps = Readonly<{
  tabId: string;
  workspaceType: WorkspaceType;
}>;

type StateProps = Readonly<{
  isinCode?: string;
  denomination?: number;
  fileCode?: string;
  ric?: string;
  maturityDate?: string;
  productClients?: ISalesCounterpart[];
  quotationType?: SecondaryQuotationType;
  priceType?: SecondaryPriceType;
  currency?: string;
  clientType: ClientType;
  isClientVisible: boolean;
  traderGroup?: string;
  isSalesVisible: boolean;
  portfolio?: string;
  isOtcProduct: boolean;
  traderMessage?: string;
  saleMessage?: string;
  quantity: number | undefined;
  cashAmount: number | undefined;
  tradeWay: TradeWay;
  needCustodian: boolean;
  selectedCustodian: Custodian | undefined;
  disabledTradeWays: readonly TradeWay[];
  salesMarginPlusUpfront: number | undefined;
  upfront: number | undefined;
  salesMarginAdjusted: number | undefined;
  tradingPriceAdjusted: number | undefined;
  amountUnit: string;
  cutOffTime: string | undefined;
  cutOffZoneId: string | undefined;
  nextCutOff: Date | undefined;
  settlementDate: string | undefined;
  firstSettlementDate: string | undefined;
  settlementHolidays: ReadonlyArray<string> | undefined;
  valuationDate: string | undefined;
  tradeDateType: TradeDateType;
  availableValuationDates: ReadonlyArray<string> | undefined;
  availableSettlementTypes: ReadonlyArray<CpOrderSettlement> | undefined;
  settlementType: string | undefined;
  cobDate: string | undefined;
  isLoadingSettlement: boolean;
  clientLoadingStatus: LoadingStatus;
  client?: ISalesCounterpart;
  clientEliotCode?: string;
  productOtcClients?: ReadonlyArray<OtcClient>;
  salesId?: string;
  availableSales: Sales[];
  selectedSalesDesk?: string;
  availableDesks: readonly SalesDesk[];
  isFrozen: boolean;
  tradeSizeInputMode: TradeSizeInputMode;
  minQuantity?: number;
  maxQuantity?: number;
  lot?: number;
  remaining?: number;
}>;

type DispatchProps = Readonly<{
  onClientTypeChanged: (value: ClientType) => void;
  onQuantityChanged: (value: number | undefined) => void;
  onQuantityReset: () => void;
  onCashAmountReset: () => void;
  onCashAmountChanged: (value: number | undefined) => void;
  onCustodianChanged: (value: Custodian | undefined | null) => void;
  onTradingPriceChanged: (value: number | undefined) => void;
  onSalesMarginChanged: (value: number | undefined) => void;
  onSettlementDateChanged: (value: Nullable<string>) => void;
  onValuationDateChanged: (value: Nullable<string>) => void;
  onSettlementTypeChanged: (value: string | undefined) => void;
  onTradeDateTypeChanged: (value: TradeDateType) => void;
  onTradeSizeInputModeChanged: () => void;
  onClientChanged: (client: ISalesCounterpart) => void;
  onClientClear: () => void;
  onRetryLoadingClient: () => void;
  onSalesChanged: (salesId: string | undefined) => void;
  onSalesDeskChanged: (salesId: string | undefined) => void;
}>;

export type CpOrderFormContentProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps: MapStateToPropsFactory<StateProps, OwnProps, AppState> = () => {
  const getDisabledTradeways = createSelector([getAllowedTradeWays], allowedTradeways =>
    allTradeWays.filter(way => !allowedTradeways.includes(way)),
  );
  const getAvailableSales = createSelector(
    [
      (appState: AppState, tabId: string) =>
        fromSecondaryMarket.cpOrder.referential.client.getAllSales(appState, tabId),
      (appState: AppState, tabId: string) => fromSecondaryMarket.cpOrder.form.getFormSalesDesk(appState, tabId),
    ],
    (allSales, selectedSalesDesk) => getAvailableSalesWithSelecteDesk(allSales, selectedSalesDesk),
  );

  const getDesks = createSelector(
    [
      (appState: AppState, tabId: string) =>
        fromSecondaryMarket.cpOrder.referential.client.getAllSales(appState, tabId),
    ],
    allSales => getAvailableDesks(allSales),
  );

  return (state, { tabId }) => {
    const bcsProductKey = fromSecondaryPostEventsWorkspace.getBcsProductKey(state, tabId);
    const cashAmount = fromSecondaryMarket.cpOrder.form.getFormCashAmount(state, tabId);
    const quantity = fromSecondaryMarket.cpOrder.form.getFormQuantity(state, tabId);
    const clientType = fromSecondaryMarket.cpOrder.form.getFormClientType(state, tabId)!;
    const selectedSalesDesk = fromSecondaryMarket.cpOrder.form.getFormSalesDesk(state, tabId);
    const salesId = fromSecondaryMarket.cpOrder.form.getFormSalesId(state, tabId);
    const salesMarginAdjusted = fromSecondaryMarket.cpOrder.form.getFormSalesMargin(state, tabId);
    const valuationDate = fromSecondaryMarket.cpOrder.form.getFormValuationDate(state, tabId);
    const settlementDate = fromSecondaryMarket.cpOrder.form.getFormSettlementDate(state, tabId);
    const firstSettlementDate = fromSecondaryMarket.cpOrder.form.getFormFirstSettlementDate(state, tabId);
    const tradeWay = fromSecondaryMarket.cpOrder.form.getFormTradeWay(state, tabId)!;
    const tradeDateType = fromSecondaryMarket.cpOrder.form.getTradeDateType(state, tabId)!;
    const selectedSettlementType = fromSecondaryMarket.cpOrder.form.getFormSettlementType(state, tabId);
    const defaultSettlementType = fromSecondaryMarket.cpOrder.referential.client.getClientDefaultSettlementType(
      state,
      tabId,
      tradeWay,
    );
    const settlementType = toSettlementTypeKey(selectedSettlementType ?? defaultSettlementType);
    const isFrozen = getTabIsFrozen(state, tabId) ?? false;
    const isOtcProduct = isOtcBcsProductKey(bcsProductKey);
    const isinCode = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductIsin(state, tabId);
    const ric = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductRic(state, tabId);
    const fileCode = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductFileCode(state, tabId);
    const denomination = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductDenomination(
      state,
      tabId,
    )!;
    const currency = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductCurrency(state, tabId);
    const maturityDate = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductMaturityDate(
      state,
      tabId,
    )!;
    const quotationType = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductQuotationType(
      state,
      tabId,
    )!;
    const priceType = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductPriceType(state, tabId)!;
    const productClients = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductClients(state, tabId)!;
    const traderGroup = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductTraderGroup(state, tabId)!;
    const traderMessage = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductTraderMessage(
      state,
      tabId,
    );
    const saleMessage = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductSalesMessage(state, tabId);
    const selectedCustodian = fromSecondaryMarket.cpOrder.form.getFormCustodian(state, tabId);
    const client = fromSecondaryMarket.cpOrder.referential.client.getAstroClient(state, tabId);
    const clientEliotCode = fromSecondaryMarket.cpOrder.form.getFormClientEliotCode(state, tabId);
    const hasSelectedClient = fromSecondaryMarket.cpOrder.form.hasSelectedClient(state, tabId);
    const disabledTradeWays = getDisabledTradeways(state, tabId);
    const extraAsk = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductExtraAsk(state, tabId);
    const extraBid = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductExtraBid(state, tabId);
    const defaultUpfront = getRelevantPriceDependingOnTradeWay(tradeWay, { bid: extraBid, ask: extraAsk });
    const bcsProductSalesMargin = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductSalesMargin(
      state,
      tabId,
    );
    const upfront = fromSecondaryMarket.cpOrder.form.getFormUpfront(state, tabId) ?? defaultUpfront;
    const salesMarginPlusUpfront = getSalesMarginPlusUpfront(bcsProductSalesMargin, upfront);
    const amountUnit = getAmountUnit(priceType, currency!);
    const cutOffTime = fromSecondaryMarket.cpOrder.referential.product.getBookingParamsCutoffLimitTime(state, tabId);
    const cutOffZoneId = fromSecondaryMarket.cpOrder.referential.product.getBookingParamsCutoffZoneId(state, tabId);
    const settlementHolidays = fromSecondaryMarket.cpOrder.referential.product.getBookingParamsSettlementHolidays(
      state,
      tabId,
    );
    const availableValuationDates =
      fromSecondaryMarket.cpOrder.referential.product.getBookingParamsAvailableValuationDates(state, tabId);
    const cobDate = getCobDate(valuationDate);
    const availableSettlementTypes = fromSecondaryMarket.cpOrder.referential.client.getClientSettlementTypes(
      state,
      tabId,
    );
    const isLoadingSettlement = fromSecondaryMarket.cpOrder.referential.client.isClientLoading(state, tabId);
    const clientLoadingStatus = fromSecondaryMarket.cpOrder.referential.client.getClientLoadingStatus(state, tabId);
    const availableDesks = getDesks(state, tabId);
    const allSalesFromSelectedDesk = getAvailableSales(state, tabId);
    const tradingPriceAdjusted = fromSecondaryMarket.cpOrder.form.getFormTradingPrice(state, tabId);
    const portfolio = fromSecondaryMarket.cpOrder.referential.product.getCpOrderBcsProductPortfolio(state, tabId)!;
    const productOtcClients = fromSecondaryMarket.cpOrder.referential.product.getBookingParamsOtcDealPositions(
      state,
      tabId,
    );
    const tradeSizeInputMode = fromSecondaryMarket.cpOrder.formInput.getInputFormTradeSizeInputMode(state, tabId);

    return {
      isOtcProduct,
      isinCode,
      fileCode,
      ric,
      denomination,
      currency,
      maturityDate,
      quotationType,
      priceType,
      clientType,
      productClients,
      isClientVisible: clientType === 'Other',
      traderGroup,
      isSalesVisible: hasSelectedClient,
      traderMessage,
      saleMessage,
      selectedCustodian,
      quantity,
      tradeWay,
      needCustodian: clientType === 'Other',
      disabledTradeWays,
      salesMarginPlusUpfront,
      upfront,
      amountUnit,
      cutOffTime,
      cutOffZoneId,
      nextCutOff: undefined,
      settlementDate,
      firstSettlementDate,
      settlementHolidays,
      availableValuationDates,
      valuationDate,
      tradeDateType,
      cobDate,
      settlementType,
      availableSettlementTypes,
      isLoadingSettlement,
      clientLoadingStatus,
      clientEliotCode,
      productOtcClients,
      client,
      availableDesks,
      availableSales: allSalesFromSelectedDesk,
      salesId,
      selectedSalesDesk,
      isFrozen,
      cashAmount,
      salesMarginAdjusted,
      tradingPriceAdjusted,
      portfolio,
      tradeSizeInputMode,
      minQuantity: getCpOrderReferentialMinQuantity(state, tabId),
      maxQuantity: getMaxQuantity(state, tabId),
      remaining: getCpOrderReferentialRemaining(state, tabId),
      lot: getCpOrderReferentialLotSize(state, tabId),
    };
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch, { tabId }: OwnProps): DispatchProps => {
  return {
    onClientTypeChanged: value => {
      dispatch(secondaryMarketCpOrderClientTypeSelectedThunk(tabId, 'secondaryMarketPostEvents', value));
    },
    onCashAmountChanged: value => {
      dispatch(secondaryMarketCpOrderCashAmountChangedThunk(tabId, value));
    },
    onCashAmountReset: () => {
      dispatch(secondaryMarketCpOrderCashAmountResetThunk(tabId));
    },
    onQuantityChanged: value => {
      dispatch(secondaryMarketCpOrderQuantityChangedThunk(tabId, value));
    },
    onQuantityReset: () => {
      dispatch(secondaryMarketCpOrderQuantityResetThunk(tabId));
    },
    onCustodianChanged: value => {
      dispatch(secondaryMarketCpOrderCustodianSelectedThunk(tabId, 'secondaryMarketPostEvents', value));
    },
    onTradingPriceChanged: value => {
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'tradingPrice', value));
    },
    onSalesMarginChanged: value => {
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'salesMargin', value));
    },
    onSettlementDateChanged(value) {
      if (value !== null) {
        dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'settlementDate', value));
      }
    },
    onValuationDateChanged(value) {
      if (value !== null) {
        dispatch(secondaryMarketCpOrderFormValuationDateChangedThunk(tabId, 'secondaryMarketPostEvents', value));
      }
    },
    onTradeDateTypeChanged(value) {
      dispatch(secondaryMarketCpOrderFormTradeDateTypeChangedThunk(tabId, 'secondaryMarketPostEvents', value));
    },
    onSettlementTypeChanged(value) {
      dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'settlementType', fromSettlementTypeKey(value)));
    },
    onClientChanged: client => {
      dispatch(secondaryMarketCpOrderClientSelectedThunk(tabId, 'secondaryMarketPostEvents', client));
    },
    onClientClear: () => {
      dispatch(secondaryMarketCpOrderClientSelectedThunk(tabId, 'secondaryMarketPostEvents', undefined));
    },
    onRetryLoadingClient: () => {
      dispatch(secondaryMarketCpFormLinkedToClientRequested(tabId));
    },
    onSalesChanged: salesId => {
      dispatch(secondaryMarketCpOrderSelectSalesIdThunk(tabId, salesId));
    },
    onSalesDeskChanged: salesDesk => {
      dispatch(secondaryMarketCpOrderSelectSalesDeskThunk(tabId, salesDesk));
    },
    onTradeSizeInputModeChanged() {
      dispatch(secondaryMarketCpOrderTradeSizeInputModeChangedThunk(tabId));
    },
  };
};

export const connectCpOrderFormContent = connect(mapStateToProps, mapDispatchToProps);
